import {
    FC,
    MouseEventHandler,
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from "react";
import Table from "../../../assets/components/Table/Table";
import { ColumnDef } from "@tanstack/react-table";
import "../../../assets/components/Table/Table_header.scss";
import { Permissions } from "../../../models/models";
import {
    useCreateMigratedProductMutation,
    useDeleteMigratedProductMutation,
    useGetMigratedProductsListQuery,
    useGetProductsListQuery,
    useUpdateMigratedProductMutation,
    useUploadCSVMigratedProductsMutation,
} from "../../../redux/RTKQuery/api";
import { useAppDispatch, useAppSelector } from "../../../assets/hooks/hooks";
import { Loading, uiSlicedActions } from "../../../redux/ui-slice";
import Loader from "../../../assets/components/Loader/Loader";

import "./MigratedProducts.scss";
import RemoveButton from "../../../assets/components/RemoveButton/RemoveButton";
import {
    hasPermission,
    prepareCSVUploadData,
} from "../../../assets/helpers/helpers";
import ArrowSprite from "../../../assets/components/Table/Img/arrow_sprite.svg";
import { useNotification } from "../../../assets/hooks/useNotification";
import { actionText } from "../../../text";
import TableHeader from "../../../assets/components/Table/TableHeader";
import ImageSprite from "../../../assets/components/ImageSprite/ImageSprite";
import PageTitle from "../../../assets/components/PageTitle/PageTitle";
import {
    MigratedProductColumnsTable,
    MigratedProductTable,
    RenderSubComponentMigratedProductTable as RenderSubComponent,
    ActualProduct,
} from "./model";
import MigratedProductSubRow from "./MigratedProductSubRow/MigratedProductSubRow";
import {
    Page,
    PaginationBar,
} from "../../../assets/components/PaginationBar/PaginationBar";
import {
    SearchBar,
    SearchBarDataHandler,
} from "../../../assets/components/SearchBar/SearchBar";
import { toast } from "react-toastify";
import {
    defActualProducts,
    defColumnsDataTable,
} from "./MigratedProductsHelpers";

type Removed = {
    upclickProductId: string;
    deleteProductHandler: (upclickProductId: string) => void;
};

const RemovedPost: FC<Removed> = ({
    upclickProductId,
    deleteProductHandler,
}) => {
    const { AttentionMessageBox } = useNotification();
    return (
        <RemoveButton
            position="top_center"
            img="top"
            onClick={() => {
                AttentionMessageBox({
                    textContent: actionText("delete", "migrated product"),
                }).then((value) => {
                    if (value === "yes") {
                        deleteProductHandler(upclickProductId);
                    }
                });
            }}
        />
    );
};

const renderSubComponent: RenderSubComponent<ActualProduct[]> = ({
    row,
    subComponentProps,
    subRowHandler,
}) => {
    const closeFormHandler = (id: number) => {
        row.toggleExpanded();

        if (id === -1 && subRowHandler) {
            subRowHandler(id);
        }
    };

    return (
        <MigratedProductSubRow
            {...row.original}
            actualProducts={subComponentProps}
            closeFormHandler={closeFormHandler}
        />
    );
};

const MigratedProducts: FC = () => {
    const user = useAppSelector((state) => state.initializeApp);

    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.PRODUCT_DELETE
    );

    const [
        deleteMigratedProduct,
        {
            isError: isErrorDeleteProduct,
            isSuccess: isSuccessDeleteProduct,
            isLoading: isLoadingDeleteProduct,
        },
    ] = useDeleteMigratedProductMutation();

    useEffect(() => {
        if (isSuccessDeleteProduct) {
            console.log("Product was successfully removed");
            toast("The Product was successfully removed!", { type: "success" });
        } else if (isErrorDeleteProduct) {
            console.log("Product was not removed");
            toast("The Product was not removed!", { type: "error" });
        }
    }, [isErrorDeleteProduct, isSuccessDeleteProduct]);

    const columns: ColumnDef<MigratedProductColumnsTable>[] = [
        {
            header: () => <span>ID</span>,
            accessorKey: "upclickProductId",
            cell: ({ row, getValue }) => {
                return row.original.productId !== -1
                    ? getValue<string>()
                    : "Add Product";
            },
        },
        {
            header: () => <span>Original Product Name</span>,
            accessorKey: "upclickProductName",
        },

        {
            header: () => <span>Actual Product Name</span>,
            accessorKey: "productName",
        },
        {
            id: "removed",
            header: () => null,
            cell: ({ row }) => {
                return isAccessPermission &&
                    row.original.productId !== -1 &&
                    row.original.canBeDeleted ? (
                    <RemovedPost
                        upclickProductId={row.original.upclickProductId}
                        deleteProductHandler={(upclickProductId) => {
                            deleteMigratedProduct(upclickProductId);
                        }}
                    />
                ) : null;
            },
        },
        {
            id: "expander",
            header: () => null,
            cell: ({ row }) => {
                const editButtonIcon = row.getIsExpanded()
                    ? "arrowUp"
                    : "arrowDown";
                return row.original.productId !== -1 ? (
                    <button
                        className="edit_button"
                        {...{
                            onClick: () => row.getToggleExpandedHandler(),
                            style: { cursor: "pointer" },
                        }}
                    >
                        <ImageSprite
                            iconSprite={`${ArrowSprite}#${editButtonIcon}`}
                        />
                    </button>
                ) : null;
            },
        },
    ];

    const dispatch = useAppDispatch();
    const pageSize: number = 30;
    const [currentPageSize, setCurrentPageSize] = useState(pageSize);
    const [page, setCurrentPage] = useState<Page>(1);

    const [totalMigratedProducts, setTotalMigratedProducts] =
        useState<number>(0);

    const [dataTable, setDataTable] = useState<MigratedProductColumnsTable[]>(
        []
    );
    const [dataTableProps, setTableProps] = useState<ActualProduct[]>([]);
    const loading: Loading = useAppSelector((state) => state.ui.loading);
    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});

    const [
        uploadCSVfile,
        {
            isLoading: isLoadingUploadCSVMigratedProducts,
            isSuccess: isSuccessUploadCSVMigratedProducts,
            isError: isErrorUploadCSVMigratedProducts,
            reset: resetUploadCSVMigratedProducts,
        },
    ] = useUploadCSVMigratedProductsMutation();

    const [
        ,
        {
            isLoading: isLoadingUpdatedMigratedProducts,
            isSuccess: isSuccessUpdatedMigratedProducts,
            isError: isErrorUpdatedMigratedProducts,
            reset: resetUpdatedMigratedProducts,
        },
    ] = useUpdateMigratedProductMutation({
        fixedCacheKey: "update_migrated_product",
    });
    const [
        ,
        {
            isLoading: isLoadingCreatedMigratedProducts,
            isSuccess: isSuccessCreatedMigratedProducts,
            isError: isErrorCreatedMigratedProducts,
            reset: resetCreatedMigratedProducts,
        },
    ] = useCreateMigratedProductMutation({
        fixedCacheKey: "create_migrated_product",
    });

    const {
        data: dataMigratedProducts,
        isLoading: isLoadingMigratedProducts,
        isSuccess: isSuccessMigratedProducts,
        isError: isErrorMigratedProducts,
    } = useGetMigratedProductsListQuery(
        {
            pageSize: pageSize,
            pageNumber: page,
            name: quiresData.searchInput,
        },
        {
            refetchOnFocus: true,
        }
    );

    const {
        data: dataProducts,
        isLoading: isLoadingProducts,
        isSuccess: isSuccessProducts,
        isError: isErrorProducts,
    } = useGetProductsListQuery(
        {},
        {
            refetchOnFocus: true,
        }
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [dataMigratedProducts?.filteredProductsCount]);

    const handleMigratedProductsData = useCallback(() => {
        if (isSuccessMigratedProducts && dataMigratedProducts) {
            const columnsDataTable = defColumnsDataTable(
                dataMigratedProducts.products
            );
            setDataTable(columnsDataTable);

            if (dataMigratedProducts.filteredProductsCount !== undefined) {
                setCurrentPageSize((currentPageSize) => {
                    if (totalMigratedProducts < pageSize) {
                        return (currentPageSize =
                            dataMigratedProducts.filteredProductsCount as number);
                    } else {
                        return pageSize;
                    }
                });
                setTotalMigratedProducts(
                    dataMigratedProducts.filteredProductsCount
                );
            } else {
                setTotalMigratedProducts(dataMigratedProducts.totalProducts);
            }
        }

        if (isErrorMigratedProducts) {
            toast.error("Error loading Migrated products");
        }
    }, [
        isSuccessMigratedProducts,
        dataMigratedProducts,
        isErrorMigratedProducts,
        totalMigratedProducts,
    ]);

    const handleProductsData = useCallback(() => {
        if (isSuccessProducts && dataProducts) {
            const actualProducts = defActualProducts(dataProducts.products);
            setTableProps(actualProducts);
        }

        if (isErrorProducts) {
            toast.error("Error loading products");
        }
    }, [dataProducts, isSuccessProducts, isErrorProducts]);

    const handleUpdatedMigratedProducts = useCallback(() => {
        if (isSuccessUpdatedMigratedProducts) {
            toast.success("The product was successfully updated!");
            resetUpdatedMigratedProducts();
        }

        if (isErrorUpdatedMigratedProducts) {
            toast.error("The product was not updated!");
            resetUpdatedMigratedProducts();
        }
    }, [
        isSuccessUpdatedMigratedProducts,
        isErrorUpdatedMigratedProducts,
        resetUpdatedMigratedProducts,
    ]);

    const handleUploadCSVMigratedProducts = useCallback(() => {
        if (isSuccessUploadCSVMigratedProducts) {
            toast.success("The products were successfully uploaded!");
            resetUploadCSVMigratedProducts();
        }

        if (isErrorUploadCSVMigratedProducts) {
            toast.error("The products were not uploaded!");
            resetUploadCSVMigratedProducts();
        }
    }, [
        isSuccessUploadCSVMigratedProducts,
        isErrorUploadCSVMigratedProducts,
        resetUploadCSVMigratedProducts,
    ]);

    const handleCreatedMigratedProducts = useCallback(() => {
        if (isSuccessCreatedMigratedProducts) {
            toast.success("The product was successfully created!");
            resetCreatedMigratedProducts();
        }

        if (isErrorCreatedMigratedProducts) {
            toast.error("The product was not created!");
            resetCreatedMigratedProducts();
        }
    }, [
        isSuccessCreatedMigratedProducts,
        isErrorCreatedMigratedProducts,
        resetCreatedMigratedProducts,
    ]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal =
            isLoadingProducts ||
            isLoadingMigratedProducts ||
            isLoadingDeleteProduct ||
            isLoadingUpdatedMigratedProducts ||
            isLoadingCreatedMigratedProducts ||
            isLoadingUploadCSVMigratedProducts;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden")
        );
    }, [
        isLoadingProducts,
        isLoadingMigratedProducts,
        isLoadingDeleteProduct,
        dispatch,
        isLoadingUpdatedMigratedProducts,
        isLoadingCreatedMigratedProducts,
        isLoadingUploadCSVMigratedProducts,
    ]);

    // Handle data fetching and state updates
    useLayoutEffect(() => {
        handleMigratedProductsData();
        handleProductsData();
        handleLoadingState();
        handleUpdatedMigratedProducts();
        handleCreatedMigratedProducts();
        handleUploadCSVMigratedProducts();
    }, [
        handleMigratedProductsData,
        handleProductsData,
        handleLoadingState,
        handleUpdatedMigratedProducts,
        handleCreatedMigratedProducts,
        handleUploadCSVMigratedProducts,
    ]);

    const [isNewProductFormVisible, setNewProductFormVisible] = useState(false);

    const createNewProductHandler: MouseEventHandler<
        HTMLButtonElement
    > = () => {
        const newRow: MigratedProductColumnsTable = {
            productId: -1,
            productName: "",
            upclickProductName: "",
            upclickProductId: "",
            isOpen: true,
            canBeDeleted: true,
        };

        if (!isNewProductFormVisible) {
            setDataTable((prevData) => [newRow, ...prevData]);
            setNewProductFormVisible(!isNewProductFormVisible);
        }
    };

    const uploadProductsFileHandler = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const data: FormData = prepareCSVUploadData(e, false);

        uploadCSVfile(data);
    };

    const removeNewProductHandler = (id: number) => {
        console.log("remove new product handler: " + id);
        const data = dataTable.filter((product) => product.productId !== id);
        setDataTable(data);
        setNewProductFormVisible(!isNewProductFormVisible);
    };

    return (
        <div className="page_with_table">
            <Loader loading={loading} />

            <PageTitle text="Migrated Products" />

            <SearchBar
                dataHandler={setQuires}
                searchInput={{
                    placeholder: "Search by Upclick product name",
                }}
            />

            <div className="table_scroll_wrapper">
                <TableHeader
                    pageSize={pageSize}
                    dataTableLength={currentPageSize}
                    title="List of products"
                    onAddRowHandler={createNewProductHandler}
                    addPermission={Permissions.PRODUCT_EDIT}
                    onUploadFileHandler={uploadProductsFileHandler}
                >
                    <PaginationBar
                        currentPage={page as number}
                        currentPageHandler={setCurrentPage}
                        totalItems={totalMigratedProducts}
                        pageSize={pageSize}
                    />
                </TableHeader>

                <Table<MigratedProductTable, ActualProduct[]>
                    className="migrated_products_table"
                    data={dataTable}
                    columns={columns}
                    getRowCanExpand={() => true}
                    subRowHandler={removeNewProductHandler}
                    renderSubComponent={renderSubComponent}
                    subComponentProps={dataTableProps}
                />
            </div>
        </div>
    );
};

export default MigratedProducts;
